import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import SlidingPane from 'react-sliding-pane';
import Flatpickr from 'react-flatpickr';

const FilterSidebar = ({
  isOpen,
  onClose,
  onApply,
  title,
  subtitle,
  filterGroups,
  activeFilters,
  onFilterChange,
  children,
  showSaveButton,
  onSaveFilter
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [tempFilterValues, setTempFilterValues] = useState({});
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => {
    if (isOpen && filterGroups) {
      const initialExpanded = {};
      filterGroups.forEach(group => {
        initialExpanded[group.id] = true;
      });
      setExpandedGroups(initialExpanded);
    }
  }, [isOpen, filterGroups]);

  useEffect(() => {
    if (activeFilters) {
      setTempFilterValues(activeFilters);
    }
  }, [activeFilters]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const filterGroupsBySearch = (groups) => {
    if (!searchTerm) return groups;

    return groups.map(group => ({
      ...group,
      filters: group.filters.filter(filter =>
        filter.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(group => group.filters.length > 0);
  };

  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
    setFilterSearch('');
  };

  const handleBackClick = () => {
    setSelectedFilter(null);
    setFilterSearch('');
  };

  const handleCheckboxChange = (filterId, value, checked) => {
    setTempFilterValues(prev => {
      const currentValues = prev[filterId] || [];
      if (checked) {
        return { ...prev, [filterId]: [...currentValues, value] };
      } else {
        return { ...prev, [filterId]: currentValues.filter(v => v !== value) };
      }
    });
  };

  const handleRemoveValue = (filterId, value) => {
    setTempFilterValues(prev => {
      const currentValues = prev[filterId] || [];
      return { ...prev, [filterId]: currentValues.filter(v => v !== value) };
    });
  };

  const handleRangeChange = (filterId, field, value) => {
    setTempFilterValues(prev => {
      const currentValue = prev[filterId] || { min: '', max: '' };
      return { ...prev, [filterId]: { ...currentValue, [field]: value } };
    });
  };

  const handleDateRangeChange = (filterId, field, value) => {
    setTempFilterValues(prev => {
      const currentValue = prev[filterId] || { start: '', end: '' };
      return { ...prev, [filterId]: { ...currentValue, [field]: value } };
    });
  };


  const handleApply = () => {
    onFilterChange(tempFilterValues);
    onApply();
    onClose();
  };

  const filteredGroups = filterGroups ? filterGroupsBySearch(filterGroups) : [];

  const renderFilterDetail = (filter) => {
    const currentValues = tempFilterValues[filter.id] || [];

    const filteredOptions = (filter.options || []).filter(option =>
      option.label.toLowerCase().includes(filterSearch.toLowerCase())
    );

    return (
      <div className="flex-grow-1 overflow-auto">
        <div className="p-4 border-bottom">
          <button
            className="btn btn-link text-primary p-0 mb-3"
            onClick={handleBackClick}
          >
            <i className="fal fa-chevron-left mr-2"></i>
            Back
          </button>
          <h6 className="mb-3 font-weight-bold">{filter.label}</h6>

          {(filter.type === 'checkbox' || filter.type === 'search') && (
            <>
              <div className="mb-3">
                <label className="d-flex align-items-center">
                  <input
                    type="radio"
                    name={`${filter.id}-operator`}
                    value="any"
                    defaultChecked
                    className="mr-2"
                  />
                  <span className="text-muted">is any of</span>
                </label>
              </div>

              {currentValues.length > 0 && (
                <div className="mb-3">
                  <div className="border rounded p-2" style={{ minHeight: '40px' }}>
                    {currentValues.map(value => {
                      const matchingOption = filter.options.find(o => o.value === value);
                      const displayLabel = matchingOption ? matchingOption.label : value;
                      return (
                        <span key={value} className="badge badge-primary mr-2 mb-2">
                          {displayLabel}
                          <button
                            className="btn btn-link p-0 ml-2 text-white"
                            onClick={() => handleRemoveValue(filter.id, value)}
                            style={{ fontSize: '12px' }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {filter.type === 'search' && (
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Search ${filter.label.toLowerCase()}...`}
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                  />
                </div>
              )}

              {filteredOptions.length > 0 && (
                <div className="mb-2">
                  {filteredOptions.every(o => currentValues.includes(o.value)) ? (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => {
                        const valuesToRemove = new Set(filteredOptions.map(o => o.value));
                        setTempFilterValues(prev => ({
                          ...prev,
                          [filter.id]: (prev[filter.id] || []).filter(v => !valuesToRemove.has(v))
                        }));
                      }}
                    >
                      Deselect All
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => {
                        const allValues = new Set([...(currentValues || []), ...filteredOptions.map(o => o.value)]);
                        setTempFilterValues(prev => ({
                          ...prev,
                          [filter.id]: [...allValues]
                        }));
                      }}
                    >
                      Select All
                    </button>
                  )}
                </div>
              )}
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {filteredOptions.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No options available</p>
                    <p className="text-muted small">Options will appear once data is available</p>
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <div key={option.value} className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`${filter.id}-${option.value}`}
                        checked={currentValues.includes(option.value)}
                        onChange={(e) => handleCheckboxChange(filter.id, option.value, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`${filter.id}-${option.value}`}>
                        {option.label}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {filter.type === 'range' && (
            <>
              <div className="mb-3">
                <label className="d-flex align-items-center">
                  <input
                    type="radio"
                    name={`${filter.id}-operator`}
                    value="between"
                    defaultChecked
                    className="mr-2"
                  />
                  <span className="text-muted">is between</span>
                </label>
              </div>

              <div className="row">
                <div className="col-6">
                  <label className="small text-muted">Min</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Min"
                    value={currentValues.min || ''}
                    onChange={(e) => handleRangeChange(filter.id, 'min', e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <label className="small text-muted">Max</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Max"
                    value={currentValues.max || ''}
                    onChange={(e) => handleRangeChange(filter.id, 'max', e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {filter.type === 'date_range' && (
            <>
              <div className="mb-3">
                <label className="d-flex align-items-center">
                  <input
                    type="radio"
                    name={`${filter.id}-operator`}
                    value="between"
                    defaultChecked
                    className="mr-2"
                  />
                  <span className="text-muted">is between</span>
                </label>
              </div>

              <div className="mb-3">
                <label className="small text-muted">Start Date</label>
                <div className="flatpickr-date-input px-2 d-flex align-items-center">
                  <div className="pr-2" style={{ fontSize: 18 }}>
                    <i className="far fa-calendar" />
                  </div>
                  <Flatpickr
                    options={{ dateFormat: 'm/d/Y' }}
                    placeholder="Select date"
                    value={currentValues.start || ''}
                    onClose={(dates) => {
                      if (dates[0]) {
                        const d = dates[0];
                        const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        handleDateRangeChange(filter.id, 'start', formatted);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="small text-muted">End Date</label>
                <div className="flatpickr-date-input px-2 d-flex align-items-center">
                  <div className="pr-2" style={{ fontSize: 18 }}>
                    <i className="far fa-calendar" />
                  </div>
                  <Flatpickr
                    options={{ dateFormat: 'm/d/Y' }}
                    placeholder="Select date"
                    value={currentValues.end || ''}
                    onClose={(dates) => {
                      if (dates[0]) {
                        const d = dates[0];
                        const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        handleDateRangeChange(filter.id, 'end', formatted);
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .slide-pane__content {
            padding: 0 !important;
          }
          @media (max-width: 767px) {
            .sliding-pane-container.slide-pane {
              width: 100% !important;
            }
          }
        `}
      </style>
      <SlidingPane
        isOpen={isOpen}
        className="sliding-pane-container"
        width="480px"
        hideHeader={true}
        from="right"
        onRequestClose={onClose}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
          height: '100%',
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'flex-start',
              width: '100%'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                flex: '1 0 0'
              }}>
                <p className="figma-text semibold lg" style={{
                  lineHeight: '28px',
                  margin: 0
                }}>{title}</p>
                {subtitle && <p className="figma-text tertiary sm" style={{
                  lineHeight: '20px',
                  margin: 0
                }}>{subtitle}</p>}
              </div>
              <button
                className="btn btn-setlist-primary"
                style={{ width: 40, height: 40, padding: 10 }}
                onClick={onClose}
              >
                <i className="fas fa-arrow-right-long-to-line" style={{ color: 'var(--od-c-ffffff, #ffffff)', fontSize: '16px' }}></i>
              </button>
            </div>
            <div style={{
              height: '1px',
              width: '100%',
              backgroundColor: 'var(--od-c-e9eaeb, #E9EAEB)'
            }}></div>
          </div>

          {!selectedFilter && (
            <div style={{
              backgroundColor: 'var(--od-c-ffffff, #ffffff)',
              border: '1px solid var(--od-c-d5d7da, #d5d7da)',
              borderRadius: '8px',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              padding: '8px 16px',
              width: '100%',
              height: '48px'
            }}>
              <i className="fas fa-search figma-text quaternary" style={{ fontSize: '16px' }}></i>
              <input
                type="text"
                placeholder="Search filters"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="figma-text quaternary md"
                style={{
                  flex: '1 0 0',
                  border: 'none',
                  outline: 'none',
                  lineHeight: '24px',
                  background: 'transparent'
                }}
              />
            </div>
          )}

          {selectedFilter ? (
            renderFilterDetail(selectedFilter)
          ) : (
            <div style={{ flex: '1 0 0', overflowY: 'auto' }}>
            {children || (
              filteredGroups.map((group) => (
                <div key={group.id} className="mb-4">
                  <div className="mb-3">
                    <h6 className="mb-0 font-weight-bold text-muted small">{group.label}</h6>
                  </div>

                  <ul className="list-unstyled mb-0">
                    {group.filters.map((filter) => (
                      <li key={filter.id} className="mb-2">
                        <button
                          className="btn btn-link text-left p-0 w-100 text-dark"
                          onClick={() => handleFilterClick(filter)}
                        >
                          {filter.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}

        <div className="p-4 border-top bg-white">
          <div className="d-flex justify-content-between">
            {showSaveButton && (
              <button
                className="btn btn-link text-primary"
                onClick={onSaveFilter}
              >
                Save filter
              </button>
            )}
            <div className={showSaveButton ? '' : 'w-100'}>
              <button
                className="btn btn-setlist-secondary mr-2"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="btn btn-setlist-primary"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </SlidingPane>
    </>
  );
};

FilterSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  filterGroups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    filters: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      }))
    }))
  })),
  activeFilters: PropTypes.object,
  onFilterChange: PropTypes.func,
  children: PropTypes.node,
  showSaveButton: PropTypes.bool,
  onSaveFilter: PropTypes.func
};

FilterSidebar.defaultProps = {
  title: 'Filters',
  subtitle: 'Apply filters to table data.',
  showSaveButton: false,
  activeFilters: {},
  onFilterChange: () => {},
  onSaveFilter: () => {}
};

export default FilterSidebar;
